import i18next from "i18next"
import { initReactI18next } from "react-i18next"
import { FALLBACK_LOCALE, LOKALISE_URL } from "../consts"

import enUS from "./en-US.json"

type ProjectManifest = {
  projectName: string
  baseLocale: string
  locales: Record<string, ManifestLocale>
}

type ManifestLocale = {
  name: string
  fileName: string
  locale: string
  isRTL: boolean
}

i18next.use(initReactI18next).init({
  lng: FALLBACK_LOCALE,
  fallbackLng: FALLBACK_LOCALE,
  resources: {
    "en-US": { translation: enUS },
  },
  interpolation: {
    escapeValue: false, // not needed for react
  },
  react: {
    bindI18n: "languageChanged loaded loadedResource",
  },
})

export const changeLocale = async (locale: string) => {
  await loadLocale(locale)
  i18next.changeLanguage(locale)
}

/**
 * Loads and registers locale translations from Lokalise to local i18n instance
 */
const loadLocale = async (locale: string) => {
  const manifest = await fetchManifest()
  const { fileName } = manifest.locales?.[locale] ?? {}
  if (!fileName) {
    throw new Error("Cannot load this locale - it is not in the manifest file")
  }

  const DEEP = true
  const OVERWRITE = true

  const fetchedLocale = await fetchLocale(fileName)
  i18next.addResourceBundle(
    locale,
    "translation",
    fetchedLocale,
    DEEP,
    OVERWRITE
  )
}

/**
 * Fetches manifest file directly from the Lokalise server.
 * It lets us know which locales are available for Diploma Preview
 */
const fetchManifest = async (): Promise<ProjectManifest> => {
  const url = `${LOKALISE_URL}/manifest.json`
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Fetching manifest failed. Status: ${res.status}`)
  }
  return res.json()
}

/**
 * Fetches locale translations in form of JSON, based on the
 * locale file name from Lokalise manifest
 */
const fetchLocale = async (fileName: string) => {
  const url = `${LOKALISE_URL}/${fileName}`
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`Fetching locale failed. Status: ${res.status}`)
  }
  return res.json()
}
